import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import Table from '../common/Table/Table';

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newBody, setNewBody] = useState('');
  const [editId, setEditId] = useState(null);
  const [editTemplateName, setEditTemplateName] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchTemplates = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/api/emailTemplates');
      setTemplates(response.data);
    } catch (err) {
      setError('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleAddTemplate = async () => {
    if (!newTemplateName.trim() || !newSubject.trim() || !newBody.trim()) return;
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.post('/api/emailTemplates', {
        template_name: newTemplateName.trim(),
        subject: newSubject.trim(),
        body: newBody.trim()
      });
      setTemplates([...templates, response.data]);
      setNewTemplateName('');
      setNewSubject('');
      setNewBody('');
    } catch (err) {
      setError('Failed to add email template');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTemplate = (id) => {
    const template = templates.find(t => t.id === id);
    if (!template) return;
    setEditId(id);
    setEditTemplateName(template.template_name);
    setEditSubject(template.subject);
    setEditBody(template.body);
  };

  const handleSaveEdit = async () => {
    if (!editTemplateName.trim() || !editSubject.trim() || !editBody.trim()) return;
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.put(`/api/emailTemplates/${editId}`, {
        template_name: editTemplateName.trim(),
        subject: editSubject.trim(),
        body: editBody.trim()
      });
      const updatedTemplates = templates.map(t => t.id === editId ? response.data : t);
      setTemplates(updatedTemplates);
      setEditId(null);
      setEditTemplateName('');
      setEditSubject('');
      setEditBody('');
    } catch (err) {
      setError('Failed to update email template');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    setLoading(true);
    setError('');
    try {
      await apiClient.delete(`/api/emailTemplates/${id}`);
      setTemplates(templates.filter(t => t.id !== id));
      if (editId === id) {
        setEditId(null);
        setEditTemplateName('');
        setEditSubject('');
        setEditBody('');
      }
    } catch (err) {
      setError('Failed to delete email template');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { header: 'Template Name', className: 'name-column' },
    { header: 'Subject', className: 'subject-column' },
    { header: 'Body', className: 'body-column' },
    { header: 'Created At', className: 'created-column' },
    { header: 'Updated At', className: 'updated-column' },
    { header: 'Actions', className: 'actions-header' }
  ];

  const renderRow = (template) => (
    <>
      <td>
        {editId === template.id ? (
          <input
            type="text"
            value={editTemplateName}
            onChange={(e) => setEditTemplateName(e.target.value)}
          />
        ) : (
          template.template_name
        )}
      </td>
      <td>
        {editId === template.id ? (
          <input
            type="text"
            value={editSubject}
            onChange={(e) => setEditSubject(e.target.value)}
          />
        ) : (
          template.subject
        )}
      </td>
      <td>
        {editId === template.id ? (
          <textarea
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
          />
        ) : (
          template.body
        )}
      </td>
      <td>{new Date(template.createdAt).toLocaleString()}</td>
      <td>{new Date(template.updatedAt).toLocaleString()}</td>
      <td className="actions-cell">
        {editId === template.id ? (
          <>
            <button onClick={handleSaveEdit} disabled={loading}>Save</button>
            <button onClick={() => setEditId(null)} disabled={loading}>Cancel</button>
          </>
        ) : (
          <>
            <button onClick={() => handleEditTemplate(template.id)} disabled={loading}>Edit</button>
            <button onClick={() => handleDeleteTemplate(template.id)} disabled={loading}>Delete</button>
          </>
        )}
      </td>
    </>
  );

  return (
    <div className="email-templates-container">
      <h2>Predefined Email Templates</h2>
      {error && <div className="error-message">{error}</div>}
      <div className="add-template">
        <div>
          <label htmlFor="templateName">Template Name</label>
          <input
            id="templateName"
            type="text"
            placeholder="Enter template name"
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
            disabled={loading}
          />
        </div>

        <div>
          <label htmlFor="subject">Subject</label>
          <input
            id="subject"
            type="text"
            placeholder="Enter subject"
            value={newSubject}
            onChange={(e) => setNewSubject(e.target.value)}
            disabled={loading}
          />
        </div>

        <div>
          <label htmlFor="body">Body</label>
          <textarea
            id="body"
            placeholder="Enter body"
            value={newBody}
            onChange={(e) => setNewBody(e.target.value)}
            disabled={loading}
          />
        </div>

        <button onClick={handleAddTemplate} disabled={loading}>Add</button>
      </div>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <Table
          columns={columns}
          data={templates}
          renderRow={renderRow}
          className="email-templates-table"
        />
      )}
    </div>
  );
};

export default EmailTemplates;
